import React, { useCallback, TouchEvent, MouseEvent } from "react";
import { useAppDispatch, useAppSelector } from "stores";
import styled from "styled-components";
import store from "stores/interfaces";
import useLongTap from "hooks/longTap";
import { Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useToCDNUrl } from "hooks/useCDN";
import { ReactComponent as SelectedIcon } from "./Selected.svg";
import { ReactComponent as NotSelectedIcon } from "./NotSelected.svg";

type FileListItemProps = {
  assetId: string;
  onSelect: (file: string) => void;
  onMounted: (node: HTMLImageElement) => void;
  onUnMounted: (node: HTMLImageElement) => void;
};

const FileListItem = ({
  assetId,
  onSelect,
  onMounted,
  onUnMounted,
}: FileListItemProps) => {
  const dispatch = useAppDispatch();
  const toCDNUrl = useToCDNUrl();
  const asset = useAppSelector((state) =>
    store.getUserFileById(state, assetId)
  );
  const selectingFiles = useAppSelector((state) =>
    store.getAppState(state, "selectingFiles")
  );
  const selected = useAppSelector((state) => {
    return store.getAppState(state, "selectedFileIds").includes(assetId);
  });
  const onContextMenu = useCallback(
    (e: MouseEvent) => {
      dispatch(
        store.appStateMutate((state) => {
          state.openImageMenu = true;
          state.anchorTop = e.clientY;
          state.anchorLeft = e.clientX;
          state.openImageMenuId = assetId;
        })
      );
    },
    [assetId, dispatch]
  );

  const onRightClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      onContextMenu(e);
    },
    [onContextMenu]
  );

  const onContextMenuTouch = useCallback(
    (e: TouchEvent) => {
      const top = e.touches[0]?.clientY || 0;
      const left = e.touches[0]?.clientX || 0;

      dispatch(
        store.appStateMutate((state) => {
          state.openImageMenu = true;
          state.anchorTop = top;
          state.anchorLeft = left;
          state.openImageMenuId = assetId;
        })
      );
    },
    [assetId, dispatch]
  );
  const longTap = useLongTap(onContextMenuTouch);

  const onClick = useCallback(() => {
    if (selectingFiles) {
      dispatch(
        store.appStateMutate((state) => {
          const index = state.selectedFileIds.indexOf(assetId);
          if (index < 0) {
            state.selectedFileIds.push(assetId);
          } else {
            state.selectedFileIds.splice(index, 1);
          }
        })
      );
      return;
    }

    onSelect(asset.url || "");
  }, [onSelect, dispatch, selectingFiles, assetId, asset.url]);
  const [img, setImg] = React.useState<null | HTMLImageElement>(null);
  React.useEffect(() => {
    if (img && asset.url) {
      onMounted(img);
      return () => onUnMounted(img);
    }
  }, [img, asset.url, onUnMounted, onMounted]);
  return (
    <Tooltip title={asset.name} placement="bottom" disableTouchListener>
      <FileTile
        onClick={onClick}
        onContextMenu={onRightClick}
        {...longTap}
        tabIndex={0}
      >
        <img
          ref={(node) => setImg(node)}
          src={"/blank.gif"}
          data-src={toCDNUrl(asset.url)}
          draggable={false}
          alt={asset.name}
        />
        {!asset.uploaded && <ProcessingOverlay />}
        {selectingFiles && <Selected selected={selected} />}
      </FileTile>
    </Tooltip>
  );
};

type SelectedProps = {
  selected: boolean;
};

const Selected = ({ selected }: SelectedProps) => {
  if (selected) {
    return (
      <>
        <Overlay />
        <SelectCircleChecked />
      </>
    );
  } else {
    return <SelectCircleNotChecked />;
  }
};

const ProcessingOverlay = () => {
  const [t] = useTranslation();

  return (
    <>
      <ProcessingOverlayBackground />
      <ProcessingContent>
        <ProcessingSpinner />
        <ProcessingText>
          <Typography
            variant="caption"
            sx={{ color: "white", textAlign: "center", fontWeight: "bold" }}
          >
            {t("画像処理中...")}
          </Typography>
        </ProcessingText>
      </ProcessingContent>
    </>
  );
};

type FileListItemEmptyProps = {
  onSelect: (file: string) => void;
};

export const FileListItemEmpty = ({ onSelect }: FileListItemEmptyProps) => {
  const [t] = useTranslation();
  const selectingFiles = useAppSelector((state) =>
    store.getAppState(state, "selectingFiles")
  );

  const onClick = useCallback(() => {
    if (!selectingFiles) {
      onSelect("");
    }
  }, [selectingFiles, onSelect]);

  return (
    <FileTile onClick={onClick} disabled={selectingFiles} tabIndex={0}>
      <Typography variant="caption">{t("NOIMAGE")}</Typography>
    </FileTile>
  );
};

const FileTile = styled.div<{ disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.5%;
  width: 19%;
  height: 90px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.2);
  transition: opacity 160ms ease-out;
  color: rgb(255, 255, 255);
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
  cursor: ${({ disabled }) => (disabled ? "auto" : "pointer")};
  &:active {
    opacity: 0.5;
  }
  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  background: rgba(255, 255, 255, 0.5);
`;

const ProcessingOverlayBackground = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(2px);
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ProcessingContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  animation: slideIn 0.4s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const ProcessingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const SelectCircleChecked = styled(SelectedIcon)`
  position: absolute;
  top: 4px;
  left: 4px;
`;

const SelectCircleNotChecked = styled(NotSelectedIcon)`
  position: absolute;
  top: 4px;
  left: 4px;
`;

const ProcessingText = styled.div`
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  white-space: nowrap;
`;

export default FileListItem;
